/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react';

// redux
import { useSelector, useDispatch } from "react-redux";
import {updateCachedCustomRevenueShare} from "../../../../../state/slices/settings";

// material
import {ListTable, SingleSelectDropDown } from "@insticator/insticator-ui";
import InputForm from "../../../../../material/inputform/inputForm";
import SectionGroup from "../../../../../material/expandablesection/sectiongroup/sectiongroup";
import SectionGroupDescription
  from "../../../../../material/expandablesection/sectiongroupdescription/sectiongroupdescription";
import SectionCard from "../../../../../material/expandablesection/sectioncard/sectioncard";

// utils
import {preventPasteNegative, preventSymbols} from "./utils/functions";

// consts
import {REVENUE_TYPES} from "../../../../../utils/consts";
import {CUSTOM_REVSHARE_COL_HEADS} from "../billing_const";

// styles
import injectSheet from "react-jss";
import styles from './advanced_styles';

// static
const SECTION_TITLE = 'Custom Revenue Shares'

/* ========== ~~~~~~~~~~ CUSTOM REVENUE SHARES ~~~~~~~~~~ ========== */
const CustomRevShares = injectSheet(styles)((props) => {
  const dispatch = useDispatch()
  const sites = useSelector((state) => state.account.sites)
  const customRevenueShare = useSelector((state) => state.settings.customRevenueShare)
  const customRevenueShareFromDB = useSelector((state) => state.settings.customRevenueShareFromDB)
  const [siteProductsAvailable, setSiteProductsAvailable] = useState({})

  /*************** LOCAL VARIABLES / STATE MANAGEMENT ***************/
  const popupMessage = "By deleting this product’s custom revenue share for the individual site, the revenue share for " +
                      "that product will fall back to the revenue share assigned to the account."

  const mapSites = Object.entries(sites).map(([uuid, name]) => ({
    name: name,
    value: uuid
  }))

  const products = []
  for (const [enumName, displayName] of Object.entries(REVENUE_TYPES)) {
    products.push({
      name: displayName,
      value: enumName
    })
  }

  useEffect(() => {
    let newSiteProductsAvailable = {}
    Object.keys(sites).map(siteUUID => {
      newSiteProductsAvailable = {...newSiteProductsAvailable, [siteUUID]: products}
    })
    customRevenueShareFromDB?.forEach(revShare => {
      let productsAvailable = newSiteProductsAvailable[revShare.siteUUID]
      productsAvailable = productsAvailable.filter(product => product.value !== revShare.type)
      newSiteProductsAvailable[revShare.siteUUID] = productsAvailable
    })
    setSiteProductsAvailable(newSiteProductsAvailable)
  }, [customRevenueShareFromDB])
  /*************** EVENT HANDLERS ***************/
  const handleSiteSelect = (selected) => {
    let newCustomRevenueShare = [
      ...customRevenueShare,
      {
        siteUUID: selected.value,
        id: Date.now(),
        initialLoadRevenueSharePercentage: 50.00.toFixed(2),
        refreshRevenueSharePercentage: 50.00.toFixed(2)
      }
    ]
    dispatch(updateCachedCustomRevenueShare(newCustomRevenueShare))
  }

  const handleProductSelect = (uuid, newProduct) => {
    let newCustomRevenueShare = [...customRevenueShare]
    const toUpdateIndex = newCustomRevenueShare.findIndex(object => object.id === uuid)
    const toUpdate = Object.assign({}, newCustomRevenueShare[toUpdateIndex])
    toUpdate.type = newProduct.value
    newCustomRevenueShare[toUpdateIndex] = toUpdate
    dispatch(updateCachedCustomRevenueShare(newCustomRevenueShare))

    const siteUUID = toUpdate.siteUUID
    let newSiteProductsAvailable = Object.assign({}, siteProductsAvailable)
    const productsSelected = newCustomRevenueShare.filter(revShare => revShare.siteUUID === siteUUID)
                                                  .map(revShare => revShare.type)
    newSiteProductsAvailable[siteUUID] = products.filter(product => !productsSelected.includes(product.value))
    setSiteProductsAvailable(newSiteProductsAvailable)
  }

  const handleCustomRevShareChange = (e, uuid) => {
    let val = e.target.value
    val = (val.indexOf(".") >= 0)
      ? (val.substr(0, val.indexOf(".")) + val.substr(val.indexOf("."), 3))
      : val;
    if (val > 100) {
      return
    }

    const index = customRevenueShare.findIndex(revShare => revShare.id === uuid)
    const revShareType = e.target.name
    let newCustomRevenueShare = [...customRevenueShare]
    let toUpdate = Object.assign({}, newCustomRevenueShare[index])
    toUpdate[revShareType] = val
    newCustomRevenueShare[index] = toUpdate
    dispatch(updateCachedCustomRevenueShare(newCustomRevenueShare))
  }

  const handleDelete = uuid => {
    if (window.confirm(popupMessage)) {
      const siteUUID = customRevenueShare.filter(revShare => revShare.id === uuid)[0].siteUUID
      const newCustomRevenueShare = customRevenueShare.filter(revShare => revShare.id !== uuid)
      dispatch(updateCachedCustomRevenueShare(newCustomRevenueShare))

      const newSiteProductsAvailable = Object.assign({}, siteProductsAvailable)
      const productsSelected = newCustomRevenueShare.filter(revShare => revShare.siteUUID === siteUUID)
        .map(revShare => revShare.type)
      newSiteProductsAvailable[siteUUID] = products.filter(product => !productsSelected.includes(product.value))
      setSiteProductsAvailable(newSiteProductsAvailable)
    }
  }
  /*************** FORMATTED DATA FOR LIST TABLE UI ***************/
  const formattedCustomRevShares = customRevenueShare.map(revShare => ({
    sites: mapSites.find(site => site.value === revShare.siteUUID).name,
    products: <SingleSelectDropDown
      name={'Select a product to customize'}
      items={siteProductsAvailable[revShare.siteUUID] ? siteProductsAvailable[revShare.siteUUID]: products}
      onSelect={product => handleProductSelect(revShare.id, product)}
      selected={products.find(product => product.value === revShare.type)}
      searchable={false}
    />,
    initialLoadRevShare: <InputForm name={'initialLoadRevenueSharePercentage'}
                                    value={revShare.initialLoadRevenueSharePercentage}
                                    isNumber={true}
                                    handleChange={e => handleCustomRevShareChange(e, revShare.id)}
                                    handlePaste={preventPasteNegative}
                                    handleKeyPress={preventSymbols}/>,
    refreshRevShare: <div className="trashIcon">
                      <InputForm name={'refreshRevenueSharePercentage'}
                                 value={revShare.refreshRevenueSharePercentage}
                                 isNumber={true}
                                 handleChange={e => handleCustomRevShareChange(e, revShare.id)}
                                 handlePaste={preventPasteNegative}
                                 handleKeyPress={preventSymbols}/>
                      <i className={"fas fa-trash"} onClick={e => handleDelete(revShare.id)}></i>
                    </div>
  }))
  /*************** COMPONENT ***************/
  return (
    <SectionCard>
      <SectionGroup>
        <SectionGroupDescription
          title={SECTION_TITLE}
        />
        <SingleSelectDropDown
          name={'Select a site to customize'}
          klass={props.classes.overlay}
          items={mapSites}
          onSelect={handleSiteSelect}
          searchable={false}
        />
        <ListTable
          columnHeads={CUSTOM_REVSHARE_COL_HEADS}
          data={formattedCustomRevShares}
          size={'full'}
        />
      </SectionGroup>
    </SectionCard>
  )
})

export default CustomRevShares;